// The application form in four steps — one engine for every form that asks for an application (29 Oct 2026):
//
//   the website   /rent/:slug/viewing and /rent/:slug/apply (components/site/ApplicationForm.tsx SiteApplicationSteps,
//                 the site kit)
//   staff/agents  Register for home (ReferDrawer), New viewing request (ViewingsTab), the viewing drawer's Edit
//                 (ViewingDrawer) and the agent portal's Book a viewing (AgentDashboard) — the admin kit
//                 (components/lettings/ApplicationFields.tsx ApplicationSteps / StepButtons)
//
// The rules are lib/applicationForm.ts fieldErrors (the same as applicant.schemas.ts websiteFormSchema on the server) plus
// the documents below; a step is the set of field keys it holds (stepErrors), so no rule is written twice. useFormSteps
// is the gating: Next stays off until the step's fields are right, Back keeps everything, a refused submit goes to the
// step holding the field it is about and says so under it. A form may also hold a step back for a reason that is not a
// field's problem (`blocks` — moneyBlock: the income does not cover the rent and there is no guarantor).
import { useEffect, useRef, useState, type FormEvent, type RefObject } from 'react';
import { affordability, fieldErrors, refillErrors, slotErrors, stepErrors, stepOfField, firstFailingStep, serverFieldError, toPayload, FORM_STEPS, type ApplicationState, type FormMode } from './applicationForm';
import { findNationality, needsShareCode } from './nationalities';
import type { Upload } from './reservations';

// ── The documents sent with the form: the passport, and a right to rent document unless British or Irish ───────────

/** The passport (`id`) and right to rent document (`rtr`) picked on step 2. */
export type FormDocs = { id?: Upload | null; rtr?: Upload | null };
/** 7 MB per file, the server's limit (utils/storage.ts MAX_FILE_BYTES); a PDF or a photo. */
export const DOC_MAX_BYTES = 7 * 1024 * 1024;
export const DOC_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/heic'];
/** A picked file's problem (type or size), or null. */
export const docFileError = (file: File) =>
  !DOC_TYPES.includes(file.type) ? 'Upload a PDF or a photo (JPEG, PNG, WebP or HEIC)'
    : file.size > DOC_MAX_BYTES ? 'That file is larger than 7 MB — try a smaller photo or a PDF' : null;
/** Whether this nationality needs a right to rent document besides the passport (everyone but British and Irish). */
export const needsRtrDocument = (nationality: string) => needsShareCode(nationality);
/**
 * Problems with the documents, by field: `passport`, `rtrDocument` — only once a nationality from the list is picked.
 * `onFile`: the categories already held (a viewing request's own uploads, kept on the lead), which count as given.
 */
export function formDocErrors(nationality: string, docs: FormDocs, onFile: string[] = []): Record<string, string> {
  if (!findNationality(nationality)) return {};
  const e: Record<string, string> = {};
  if (!docs.id && !onFile.includes('id')) e.passport = 'Upload your passport';
  if (needsRtrDocument(nationality) && !docs.rtr && !onFile.includes('rtr')) e.rtrDocument = 'Upload your right to rent document';
  return e;
}
/** The `documents` array for the POST: the passport, plus the right to rent document when this nationality needs one. */
export function formDocsPayload(nationality: string, docs: FormDocs): Array<Upload & { category: 'id' | 'rtr' }> {
  const out: Array<Upload & { category: 'id' | 'rtr' }> = [];
  if (docs.id) out.push({ category: 'id', ...docs.id });
  if (docs.rtr && needsRtrDocument(nationality)) out.push({ category: 'rtr', ...docs.rtr });
  return out;
}

/**
 * Every problem with the form, by field (the keys of fieldErrors). Mode viewing is mode apply's rules exactly, plus the
 * slots (`slots: false` — a form whose time is picked outside the steps leaves them out). With `docs` (a new application
 * or viewing request), the documents too. `asked`: an "Ask to refill" being answered (refillErrors).
 */
export function siteFormErrors(s: ApplicationState, advert: number, opts: { mode?: FormMode; asked?: string[] | null; docs?: FormDocs; onFile?: string[]; slots?: boolean } = {}): Record<string, string> {
  const docErrs = opts.docs ? formDocErrors(s.nationality, opts.docs, opts.onFile) : {};
  if (opts.mode === 'viewing') return { ...fieldErrors(s, advert, 'apply'), ...(opts.slots === false ? {} : slotErrors(s)), ...docErrs };
  return { ...(opts.asked ? refillErrors(s, advert, opts.asked) : fieldErrors(s, advert)), ...docErrs };
}

/**
 * A viewing request saved with the contact details only (staff or an agent logging a phone call): the name, email and
 * mobile — step 1's own fields less the date of birth and the household. The rest is asked for before it can become an
 * application (convertLead holds it to the full rules).
 */
export function contactOnlyErrors(s: ApplicationState): Record<string, string> {
  const all = fieldErrors(s, 0, 'apply');
  const out: Record<string, string> = {};
  for (const k of ['firstName', 'lastName', 'email', 'mobile']) if (all[k]) out[k] = all[k]!;
  return out;
}

/**
 * The body's `form` for "Save with contact details only" (sent with `contactOnly: true`; schemas.ts contactOnlyViewingFormSchema):
 * whatever has been typed, in the staff shape (optional sections only when filled, consents only when ticked). The move is
 * left out unless `keepMove` (an edited request that already had its move-in) — a date nobody asked them is not recorded.
 */
export function contactOnlyPayload(s: ApplicationState, opts: { keepMove?: boolean } = {}) {
  const { move, ...rest } = toPayload(s, { mode: 'viewing' });
  return opts.keepMove ? { ...rest, move } : rest;
}

// ── The affordability gate (staff and agent forms, 3 Nov 2026) ─────────────────────────────────────────────────────

/** The message on the disabled Next of the Money step. */
export const NEEDS_GUARANTOR = 'Income does not cover the rent — add a guarantor';
/**
 * The Money step (2) is held back while the income entered does not cover the rent and "They have a guarantor" is not
 * ticked — the same sum as the amber line under the income (ApplicationFields blocks.money: the rent offered, else the
 * advert). No advert, or no income typed yet: nothing to compare, no block. For useFormSteps' `blocks`.
 */
export function moneyBlock(s: ApplicationState, advert: number): Record<number, string> {
  if (!advert || s.guarantor) return {};
  const a = affordability(s, Math.round(Number(s.offeredRent || 0) * 100) || advert);
  return a.incomeMinor > 0 && !a.affordable ? { [FORM_STEPS.indexOf('Money')]: NEEDS_GUARANTOR } : {};
}

// ── The gating ────────────────────────────────────────────────────────────────────────────────────────────────────

export interface FormSteps {
  /** 0–3. */ step: number;
  last: boolean;
  /** Every field on this step is right: Next / Submit may go. */ stepOk: boolean;
  /** Why this step may not go on although its fields are right (a `blocks` entry) — the disabled Next's title. */ blocked: string | null;
  /** The name of the next step ("Money"); the Next button says just "Next" since 3 Nov 2026. */ nextName: string | null;
  /** A field's problem, once the person has been in it (or a refused submit named it). */ show: (k: string) => string | undefined;
  touch: (k: string) => void;
  /** Wrap the form's setters: editing clears the server's word on the field. */ edited: () => void;
  go: (i: number) => void;
  back: () => void;
  /** Next on a step that is right; on the last step, the whole form checked, then onSubmit. */ submit: (e?: FormEvent) => void;
  /** Every problem on these fields shows (a secondary action refused, e.g. Save with contact details only). */ touchFields: (keys: string[]) => void;
  /** The element the steps scroll into view (the form, or the block that holds the steps). */ top: RefObject<HTMLElement | null>;
}

/** "Step 2 of 4" — above the steps, or in the drawer's header (ReferDrawer). */
export const stepLabel = (steps: Pick<FormSteps, 'step'>) => `Step ${steps.step + 1} of ${FORM_STEPS.length}`;

/** Scroll `el` into view within its scrolling drawer or the page, when its top is out of sight. */
function scrollTopIntoView(el: HTMLElement) {
  let p: HTMLElement | null = el.parentElement;
  while (p && !/(auto|scroll)/.test(getComputedStyle(p).overflowY)) p = p.parentElement;
  const limit = p ? p.getBoundingClientRect().top : 0;
  if (el.getBoundingClientRect().top < limit) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * The four steps' state: which step, what has been touched, the server's last word. `errs`: every problem with the form
 * (siteFormErrors, plus anything the form adds with a key stepOfField knows); `error`: the last refused submit.
 */
export function useFormSteps(errs: Record<string, string>, opts: { error?: unknown; onSubmit: () => void; blocks?: Record<number, string> }): FormSteps {
  const [step, setStep] = useState(0);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [srv, setSrv] = useState<{ field: string; message: string } | null>(null);
  const top = useRef<HTMLElement>(null);
  // After a step change, the top of the form comes into view; after a refused submit, the field it is about.
  const [scrollTo, setScrollTo] = useState<'top' | 'invalid' | null>(null);
  const stepOk = !Object.keys(stepErrors(errs, step)).length;
  const last = step === FORM_STEPS.length - 1;
  const blocks = opts.blocks ?? {};
  const blocked = blocks[step] ?? null;

  useEffect(() => {
    const f = serverFieldError(opts.error);
    if (!f) return;
    setSrv(f);
    setTouched((t) => ({ ...t, [f.field]: true }));
    setStep(stepOfField(f.field));
    setScrollTo('invalid');
  }, [opts.error]);
  useEffect(() => {
    if (!scrollTo) return;
    const el = scrollTo === 'invalid' ? top.current?.querySelector('[data-invalid]') : null;
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    else if (top.current) scrollTopIntoView(top.current);
    setScrollTo(null);
  }, [scrollTo, step]);

  const touchFields = (keys: string[]) => setTouched((t) => ({ ...t, ...Object.fromEntries(keys.map((k) => [k, true])) }));
  const touchStep = (i: number) => touchFields(Object.keys(stepErrors(errs, i)));
  const go = (i: number) => { setStep(i); setScrollTo('top'); };
  return {
    step, last, stepOk, blocked, nextName: last ? null : FORM_STEPS[step + 1] ?? null, top,
    show: (k) => (touched[k] ? errs[k] ?? (srv?.field === k ? srv.message : undefined) : undefined),
    touch: (k) => setTouched((t) => (t[k] ? t : { ...t, [k]: true })),
    edited: () => { if (srv) setSrv(null); },
    go, back: () => go(Math.max(0, step - 1)), touchFields: (keys) => { touchFields(keys); setScrollTo('invalid'); },
    submit: (e) => {
      e?.preventDefault();
      if (!stepOk) { touchStep(step); setScrollTo('invalid'); return; }
      if (blocked) return;
      if (!last) { go(step + 1); return; }
      const bad = firstFailingStep(errs);
      if (bad >= 0) { touchStep(bad); setStep(bad); setScrollTo('invalid'); return; }
      // A block on an earlier step (the guarantor unticked after going on): back to it, where the reason shows.
      const held = Object.keys(blocks).map(Number).sort((x, y) => x - y)[0];
      if (held !== undefined && held < step) { go(held); return; }
      opts.onSubmit();
    },
  };
}
